import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function SignupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '',
    phone: '', address: '', company_name: '', entity_type: 'individual',
    paper_agreement_confirmed: false, lawful_basis_confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/invites/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid invite link');
        setInvite(data);
        setForm(f => ({
          ...f,
          email: data.email,
          full_name: data.introducer_name || '',
          phone: data.contact_phone || '',
        }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const waived = invite && invite.agreement_required === false;

  const handleSubmit = async () => {
    if (!form.full_name.trim()) return setError('Please enter your full name');
    if (!form.password) return setError('Please input a password');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    if (waived) {
      if (!form.phone.trim()) return setError('Phone number is required');
      if (form.entity_type === 'company' && !form.company_name.trim()) {
        return setError('Please enter your company name, or switch to "Individual" above');
      }
      if (!form.paper_agreement_confirmed || !form.lawful_basis_confirmed) {
        return setError('Please tick both confirmations to continue');
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/invites/${token}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      setDone(true);
      if (data.agreement_required === false) {
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setTimeout(() => navigate(`/agreement/${token}`), 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) return <div style={styles.center}>Validating invite link...</div>;
  if (error && !invite) return <div style={styles.center}><p style={styles.error}>{error}</p></div>;
  if (done) return (
    <div style={styles.center}>
      <h2 style={{ color: '#10b981' }}>✅ Account created!</h2>
      <p style={{ color: '#4b5a6e' }}>
        {waived
          ? 'You’re all set — taking you to the login page.'
          : 'Taking you to your introducer agreement — 3 short steps.'}
      </p>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>BrokerConnect</div>
        <h2 style={styles.title}>Create your account</h2>
        <p style={styles.sub}>
          You've been invited by <strong>{invite.broker_name}</strong>
          {waived && <> — your introducer agreement is already in place, so this only takes a minute.</>}
        </p>

        <label style={styles.label}>Full Name</label>
        <input
          style={styles.input}
          placeholder="Your full name"
          value={form.full_name}
          onChange={e => setF('full_name', e.target.value)}
        />

        <label style={styles.label}>Email</label>
        <input
          style={{ ...styles.input, background: '#f3f4f6', color: '#9ca3af' }}
          value={form.email}
          readOnly
        />

        {waived && (
          <>
            <label style={styles.label}>Phone Number</label>
            <input
              style={styles.input}
              type="tel"
              placeholder="+44 7700 900000"
              value={form.phone}
              onChange={e => setF('phone', e.target.value)}
            />

            <label style={styles.label}>Are you introducing as an individual, or on behalf of a company?</label>
            <div style={styles.entityToggle}>
              <label style={{ ...styles.entityOption, ...(form.entity_type === 'individual' ? styles.entityOptionActive : {}) }}>
                <input
                  type="radio"
                  name="entity_type"
                  checked={form.entity_type === 'individual'}
                  onChange={() => setF('entity_type', 'individual')}
                  style={{ marginRight: 8 }}
                />
                Individual
              </label>
              <label style={{ ...styles.entityOption, ...(form.entity_type === 'company' ? styles.entityOptionActive : {}) }}>
                <input
                  type="radio"
                  name="entity_type"
                  checked={form.entity_type === 'company'}
                  onChange={() => setF('entity_type', 'company')}
                  style={{ marginRight: 8 }}
                />
                Company
              </label>
            </div>

            {form.entity_type === 'company' && (
              <>
                <label style={styles.label}>Company Name</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Smith Estate Agents Ltd"
                  value={form.company_name}
                  onChange={e => setF('company_name', e.target.value)}
                />

                <label style={styles.label}>Company Address <span style={styles.optional}>(optional)</span></label>
                <textarea
                  style={{ ...styles.input, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Business or correspondence address"
                  value={form.address}
                  onChange={e => setF('address', e.target.value)}
                />
              </>
            )}
          </>
        )}

        <label style={styles.label}>Password</label>
        <input
          style={styles.input}
          type="password"
          placeholder="Minimum 8 characters"
          value={form.password}
          onChange={e => setF('password', e.target.value)}
        />

        {waived && (
          <div style={styles.confirmBox}>
            <label style={styles.check}>
              <input
                type="checkbox"
                checked={form.paper_agreement_confirmed}
                onChange={e => setF('paper_agreement_confirmed', e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>I confirm I have entered into a written introducer agreement with <strong>{invite.broker_name}</strong>.</span>
            </label>
            <label style={styles.check}>
              <input
                type="checkbox"
                checked={form.lawful_basis_confirmed}
                onChange={e => setF('lawful_basis_confirmed', e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>I confirm I have a lawful basis (e.g. consent or legitimate interest) to share my clients' details through this platform.</span>
            </label>
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button onClick={handleSubmit} disabled={submitting} style={styles.button}>
          {submitting ? 'Creating account...' : 'Create Account'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0' },
  card: { background: '#fff', borderRadius: 16, padding: 40, width: 420, maxWidth: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' },
  logo: { fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#0e1b2c', margin: '0 0 8px' },
  sub: { color: '#4b5a6e', fontSize: 14, margin: '0 0 24px' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
  optional: { fontWeight: 400, color: '#9ca3af' },
  entityToggle: { display: 'flex', gap: 10, marginBottom: 16 },
  entityOption: { flex: 1, display: 'flex', alignItems: 'center', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#374151', cursor: 'pointer' },
  entityOptionActive: { borderColor: '#10b981', background: '#ecfdf5', color: '#065f46', fontWeight: 600 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box', marginBottom: 16 },
  confirmBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  check: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#374151', cursor: 'pointer', lineHeight: 1.5 },
  error: { color: '#dc2626', fontSize: 13, marginBottom: 12 },
  button: { width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }
};
